import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
  MouseEvent,
} from 'react'
import { Waypoint } from 'react-waypoint'
import { LIMITS } from '@weblite/permission'
import { EmojiPicker } from 'weblite-web-emoji'
import { Transition, useHistoryCustomBack } from 'weblite-router'
import i18next from 'i18next'
import { APP_BAR_HEIGHT } from '$common/app-bar/app-bar.constants'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { ChatListItem } from '$common/chat-list-item-new'
import { FullPageListSkeleton } from '$common/full-page-list-skeleton'
import { useMultiSelectList } from '$common/multi-select-searchable-list-page'
import { closeForwardList } from '$logic/router'
import { openRoom } from '$logic/opened-room'
import {
  RoomListItem,
  useRoomsWithSendMessagePermission,
} from '$logic/room-list/room-list.react-hook'
import {
  INCREASE_LIMIT_OFFSET,
  useItemsRenderLimit,
} from '../chat-list/react-hooks'
import { showSelectionLimitSnackbar } from '$comp/snackbars'
import { ForwardCaption } from '$comp/_app/components/leftPanel/components/forward-list/components/forward-caption'
import { editorAPI } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { ForwardListSearch } from '$comp/_app/components/leftPanel/components/forward-list/components/forward-list-search/forward-list-search'
import { AppBarBackTitleSearch } from './components/forward-list-search/components/app-bar-back-title-search'
import { useWindowHeight } from '$func/window.react-hook'
import { vibrate } from '$func/vibrate'
import { emojiI18n } from '$setup/emoji'
import { t } from '$i18n'
import { nothing } from '$helper/functions/utils'

const BASE_FORWARD_LIST_LIMIT = LIMITS.forwardDestinationCount
export const SelectionMapContext = createContext<Record<string, boolean>>({})
export const ForwardSearchQueryContext = createContext('')

const ListRenderer = memo(function ListRendererComponent({
  items,
  selectionMap,
  selectOnTap,
  loadingPermissions,
  onSelect,
  onSelectedChange,
}: {
  items: RoomListItem[]
  selectionMap: Record<string, boolean>
  selectOnTap: boolean
  loadingPermissions: boolean
  onSelect?: (roomId: string) => Promise<unknown>
  onSelectedChange: (selected: boolean, chatId: string) => void
}) {
  const selectOnTapRef = useRef<boolean>(false)
  const selectionMapRef = useRef<Record<string, boolean>>({})

  useEffect(() => {
    selectionMapRef.current = selectionMap
  }, [selectionMap])

  useEffect(() => {
    selectOnTapRef.current = selectOnTap
  }, [selectOnTap])

  const { itemsRenderLimit, increaseItemsRenderLimit, increaseNeeded } =
    useItemsRenderLimit({ totalCount: items?.length ?? 0 })

  const onClick = useCallback(
    (chatId: string) => {
      const selected = !!selectionMapRef.current[chatId]

      if (selected) onSelectedChange(false, chatId)
      else if (selectOnTapRef.current) onSelectedChange(true, chatId)
      else if (onSelect) onSelect(chatId)
      else openRoom(chatId)
    },
    [onSelectedChange, onSelect],
  )

  const onContextMenu = useCallback(
    (chatId: string) => (e: MouseEvent) => {
      e.preventDefault()
      if (!selectOnTapRef.current) onSelectedChange(true, chatId)
    },
    [onSelectedChange],
  )

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {loadingPermissions ? (
        <FullPageListSkeleton size="large" />
      ) : (
        items?.slice(0, itemsRenderLimit).map((chatItem, index) => (
          <>
            {increaseNeeded &&
              index === itemsRenderLimit - INCREASE_LIMIT_OFFSET - 1 && (
                <Waypoint onEnter={increaseItemsRenderLimit} />
              )}

            <ChatListItem
              {...chatItem}
              key={chatItem.chatId}
              selected={!!selectionMap[chatItem.chatId]}
              onClick={onClick}
              onContextMenu={onContextMenu}
              onLongPress={() => nothing}
              showWorkspaceBadge
            />
          </>
        ))
      )}
    </>
  )
})

interface Props {
  onConfirm: (selectedRooms: string[], caption?: string) => Promise<unknown>
  forwardLimit?: number
  onSelect?: (roomId: string) => Promise<unknown>
  selectOnTap?: boolean
}
export function ForwardList({
  forwardLimit,
  onConfirm,
  onSelect,
  selectOnTap = false,
}: Props) {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchMode, setSearchMode] = useState<boolean>(false)

  const { items, loadingItems, loadingPermissions } =
    useRoomsWithSendMessagePermission()

  const windowHeight = useWindowHeight()
  const listHeight = windowHeight - APP_BAR_HEIGHT

  const FORWARD_LIST_LIMIT = forwardLimit || BASE_FORWARD_LIST_LIMIT

  const selectionCountRef = useRef(0)
  const {
    selectedItems,
    selectionMap,
    setSelection,
    hasSelection,
    resetSelection,
  } = useMultiSelectList({ items, selectionPrimaryKey: 'chatId' })

  useHistoryCustomBack(() => {
    if (hasSelection) resetSelection()
    else if (searchMode) {
      setSearchMode(false)
      setSearchQuery('')
    } else closeForwardList()
  }, [searchMode, hasSelection])

  useEffect(() => {
    selectionCountRef.current =
      Object.values(selectionMap).filter(Boolean).length
  }, [selectionMap])

  const onItemSelectedChange = useCallback(
    (selected: boolean, chatId: string) => {
      if (selectionCountRef.current < FORWARD_LIST_LIMIT || !selected) {
        setSelection(chatId, selected)
        if (selected) vibrate()
      } else showSelectionLimitSnackbar(FORWARD_LIST_LIMIT)
    },
    [setSelection, FORWARD_LIST_LIMIT],
  )

  const content = searchMode ? (
    <SelectionMapContext.Provider value={selectionMap}>
      <ForwardSearchQueryContext.Provider value={searchQuery}>
        <ForwardListSearch
          hasSelection={hasSelection}
          selectionMap={selectionMap}
          onSelectedChange={onItemSelectedChange}
          onSelect={(selectedRoomId: string) => onSelect?.(selectedRoomId)}
        />
      </ForwardSearchQueryContext.Provider>
    </SelectionMapContext.Provider>
  ) : loadingItems ? (
    <FullPageListSkeleton size="large" />
  ) : items?.length >= 0 ? (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      <div
        style={{
          height: listHeight - (hasSelection ? 55 : 0),
          overflow: 'hidden auto',
        }}
      >
        <ListRenderer
          items={items}
          selectionMap={selectionMap}
          selectOnTap={selectOnTap || hasSelection}
          loadingPermissions={loadingPermissions}
          onSelectedChange={onItemSelectedChange}
          onSelect={onSelect}
        />

        <EmojiPicker
          i18n={emojiI18n}
          addEmoji={editorAPI.insertText}
          locale={i18next.language}
        />
      </div>
    </>
  ) : null

  const transitionActiveKey = searchMode ? 1 : 0

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleSearch
          title={
            hasSelection
              ? t('forwardAppBar.receiver')(selectedItems.length)
              : t('forwardAppBar.title')
          }
          onBack={
            searchMode
              ? () => {
                  setSearchMode(false)
                  setSearchQuery('')
                }
              : closeForwardList
          }
          setQuery={setSearchQuery}
          onSearch={() => {
            setSearchMode(true)
            setSearchQuery('')
          }}
          searchMode={searchMode}
        />
      }
    >
      <>
        <Transition
          activeKey={transitionActiveKey}
          name="zoomFade"
          styles={{ overflow: 'hidden' }}
        >
          {content}
        </Transition>

        {hasSelection && (
          <ForwardCaption
            selectionCount={selectedItems.length}
            onSubmit={async (caption: string) => {
              await closeForwardList()
              await onConfirm(
                selectedItems.map(({ chatId }) => chatId),
                caption,
              )
            }}
          />
        )}
      </>
    </PageWithAppBar>
  )
}
